import React from "react";
import { ImageType } from "@core/types";
import { IonSkeletonText, IonIcon } from "@ionic/react";
import { heart } from "ionicons/icons";
import { useHistory } from "react-router";
import { useIsViewFocused } from "../../../../hooks";
import { useGetIncludeProductsQuery } from "../../../../providers/products";
import { useActiveOrMainShippingZone } from "../../../../providers/shippingZones";
import { useWishListData } from "../../../../providers/wishListStore";
import { getPreviewImage } from "../../../../utils/getPreviewImage";
import { Image } from "../../../../components/Image/Image";
import { useTranslation } from "react-i18next";
import { placeholderLogo } from "../../../../parhato2/src/shared/assets/images/images";
import { useIsLg } from "../../../../hooks/useIsLg";

// Skeleton for loading
const WishlistSkeleton = () => (
  <div className="flex justify-between gap-1 p-4 rounded-2xl bg-gray-100">
    <div className="flex flex-col gap-1">
      <IonSkeletonText animated className="h-3 w-20 m-0 rounded-md" />
      <IonSkeletonText animated className="h-3 w-16 m-0 rounded-md" />
    </div>

    <div className="grid grid-cols-2 w-[6.25rem] h-[6.25rem] rounded-2xl overflow-hidden">
      {[...Array(4)].map((_, i) => (
        <IonSkeletonText key={i} animated className="h-12 w-12 rounded-md m-0" />
      ))}
    </div>
  </div>
);

const WishlistCollection = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const isViewFocused = useIsViewFocused();
  const { activeShippingZone } = useActiveOrMainShippingZone();
  const { wishListProducts: wishListProductsIds } = useWishListData();
  const isLg = useIsLg();

  // MAIN DIFFERENCE: dynamic count
  const COLLECTION_PRODUCTS_COUNT = isLg ? 9 : 4;

  const enabled = wishListProductsIds.length > 0 && isViewFocused;

  const { data: wishlistProducts, isLoading } = useGetIncludeProductsQuery({
    include: wishListProductsIds.slice(0, COLLECTION_PRODUCTS_COUNT),
    params: { zoneId: activeShippingZone.id },
    options: { enabled },
  });

  const filteredProducts = wishlistProducts?.filter((p) => p.images.length) || [];
  const placeholdersCount = COLLECTION_PRODUCTS_COUNT - filteredProducts.length;

  const getProductWord = (n: number) => {
    const d = n % 10;
    const dd = n % 100;
    if (dd >= 11 && dd <= 14) return "товаров";
    if (d === 1) return "товар";
    if (d >= 2 && d <= 4) return "товара";
    return "товаров";
  };



  const wishlistProductsQuantity = `${wishListProductsIds.length} ${getProductWord(
    wishListProductsIds.length 
  )}`;

  const handleGoToWishlist = () => history.push("/wish-list");

  // EMPTY STATE
  if (!enabled || !wishlistProducts?.length) {
    return (
      <div className="flex flex-col justify-center items-center h-[8.25rem] lg:h-[18rem] border-2 border-gray-500 border-dashed p-4 rounded-2xl">
        <div>{t("Place for your favorites")}</div>
        <IonIcon className="text-[2rem]" icon={heart} />
      </div>
    );
  }

  // LOADING
  if (isLoading) return <WishlistSkeleton />;

  return (
    <div
      className="flex items-start justify-between rounded-2xl p-2 bg-[#f4f4f4]"
      onClick={handleGoToWishlist}
    >
      {/* Left column */}
      <div className="flex flex-col">
        <div className="text-[1rem] lg:text-[1.1rem] font-semibold text-gray-700">{t("Favorites")}</div>
        <div className="text-sm  text-gray-500 font-semibold">{wishlistProductsQuantity}</div>
      </div>

      {/* Right: Product thumbnails */}
      <div
        className={`
          grid 
          grid-cols-2 gap-0.5
          w-[6.25rem] h-[6.25rem]
          lg:grid-cols-3 lg:w-[9.5rem] lg:h-[9.5rem]
          rounded-2xl overflow-hidden 
        `}
      >
        {filteredProducts.map((product) => {
          const previewSrc = getPreviewImage(product.images?.[0] as ImageType);
          return (
            <div key={product.id} className="flex items-center justify-center w-full h-full">
              <Image
                image={previewSrc}
                className="w-full h-full m-0.5"
                placeholder={placeholderLogo}
              />
            </div>
          );
        })}

        {/* placeholders */}
        {Array.from({ length: placeholdersCount }).map((_, i) => (
          <div key={i} className="flex items-center justify-center w-full h-full ">
            <Image image={undefined} className="w-full h-full m-0.5 " />
          </div>
        ))}
      </div>
    </div>
  );
};

export default WishlistCollection;
